import logging from "utils/logging";
import React from "react";
import { Box, Grid, Fade, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { selectAllPlayers } from "../player/playerSlice";
import Background from "features/components/BackgroundEffect";

const log = logging.getLogger("PodiumGrid"); //eslint-disable-line no-unused-vars

// Define brand colors
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';

const PodiumGridContainer = styled(Grid)(({ theme }) => ({
  label: "label-podium-grid-container",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(4),
  alignContent: 'flex-start',
}));

const PodiumContainer = styled('div')(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 6px 16px rgba(44,62,80,0.15)',
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  background: '#1a7a72',
  border: 'none',
}));

const ScoreContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 1),
  background: ACCENT_TEAL,
  color: '#fff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontFamily: "'Poppins', 'Inter', sans-serif",
}));

const PlayerName = styled('div')(({ theme }) => ({
  fontSize: '1.6em',
  fontWeight: 700,
  marginLeft: theme.spacing(2),
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const ScorePill = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  color: TEXT_COLOR,
  fontWeight: 700,
  fontSize: '1.1em',
  marginRight: theme.spacing(2),
  padding: theme.spacing(0.5, 0),
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

const Signature = styled('img')({
  display: "block",
  width: "100%",
  height: "250px",
  backgroundColor: "#1a7a72",
  objectFit: "contain",
});

const SignaturePlaceholder = styled(Box)({
  backgroundColor: "#1a7a72",
  objectFit: "contain",
  width: "100%",
  height: "250px",
});


export const PodiumSignature = ({ player }) => {
  const hasSignature = !!player.signatureURL;

  return hasSignature ? (
    <Fade timeout={300} in={hasSignature}>
      <Signature
        alt={`Signature for ${player.name}`}
        src={player.signatureURL}
      />
    </Fade>
  ) : (
    <Fade timeout={200} in={!hasSignature}>
      <SignaturePlaceholder />
    </Fade>
  );
};

const Podium = ({ player, podiumSize, onClick, podiumScreenRenderer }) => {
  let screen = <PodiumSignature player={player} />;

  if (podiumScreenRenderer) {
    screen = podiumScreenRenderer(player);
  }

  return (
    <Grid item xs={podiumSize}>
      <PodiumContainer>
        <ScoreContainer>
          <PlayerName>{player.name}</PlayerName>
          <ScorePill label={`${player.score.toLocaleString()} pts`} />
        </ScoreContainer>
        <Box
          width={1}
          onClick={() => (onClick ? onClick(player) : "")}
          sx={{ position: 'relative', '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            boxShadow: 'inset 0 0 4px 2px #1a7a72',
            pointerEvents: 'none',
          }}}
        >
          {screen}
        </Box>
      </PodiumContainer>
    </Grid>
  );
};

const PodiumGrid = ({ onClickPodium, players, podiumScreenRenderer }) => {
  const allPlayers = useSelector(selectAllPlayers);

  if (!players && !allPlayers) {
    return <div />;
  }

  let playersToShow = players ? players : allPlayers;
  const podiumSize = playersToShow.length <= 8 ? 3 : 2;

  return (
    <>
      <Background />
      <PodiumGridContainer container spacing={3}>
        {playersToShow &&
          playersToShow.map((player) => (
            <Podium
              player={player}
              podiumSize={podiumSize}
              onClick={onClickPodium}
              key={`podium-${player.id}`}
              podiumScreenRenderer={podiumScreenRenderer}
            />
          ))}
      </PodiumGridContainer>
    </>
  );
};

export default PodiumGrid;
